import React, { createContext, useReducer, useContext, useCallback, useRef, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_RESERVATIONS_ENDPOINT = `${process.env.REACT_APP_CURRENT_URL}/Api/reservations`;

const MaterialReservationContext = createContext();

const initialState = {
    reservations: [],
    loading: true,
    error: null,
    selectedDraaidag: null,
    currentReservation: null,
    lastFetch: null,
    cache: {}
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minuten cache

const reducer = (state, action) => {
    switch (action.type) {
        case 'FETCH_START':
            return { ...state, loading: true, error: null };
        case 'FETCH_SUCCESS':
            return { 
                ...state, 
                loading: false, 
                reservations: action.payload,
                lastFetch: Date.now(),
                cache: {
                    ...state.cache,
                    [action.cacheKey || 'default']: {
                        data: action.payload,
                        timestamp: Date.now()
                    }
                }
            };
        case 'FETCH_ERROR':
            return { ...state, loading: false, error: action.payload };
        case 'SET_SELECTED_DRAAIDAG':
            return { ...state, selectedDraaidag: action.payload, currentReservation: null }; // Clear currentReservation when draaidag changes
        case 'SET_CURRENT_RESERVATION':
            return { ...state, currentReservation: action.payload };
        case 'CLEAR_CACHE':
            return { ...state, cache: {} };
        case 'UPDATE_RESERVATION_FIELD': {
            const { reservationId, fieldName, value } = action.payload;
            let updatedCurrentReservation = state.currentReservation;

            const updatedReservations = state.reservations.map(res => {
                if (res.id === reservationId) {
                    const updatedReservation = {
                        ...res,
                        fields: {
                            ...res.fields,
                            [fieldName]: value
                        }
                    };
                    // Also update the currentReservation if it's the one being changed
                    if (state.currentReservation?.id === reservationId) {
                        updatedCurrentReservation = updatedReservation;
                    }
                    return updatedReservation;
                }
                return res;
            });

            const cacheKey = 'all_reservations';
            const cachedData = state.cache[cacheKey] || {};

            const updatedCache = {
                ...state.cache,
                [cacheKey]: {
                    ...cachedData,
                    data: updatedReservations,
                    timestamp: Date.now()
                }
            };
            
            return { ...state, reservations: updatedReservations, cache: updatedCache, currentReservation: updatedCurrentReservation };
        }
        default:
            return state;
    }
};

export const MaterialReservationProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const fetchPromiseRef = useRef(null);

    const fetchReservations = useCallback(async (forceRefresh = false) => {
        // Check cache eerst
        const cacheKey = 'all_reservations';
        const cached = state.cache[cacheKey];
        
        if (!forceRefresh && cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
            return cached.data;
        }

        // Prevent duplicate fetches
        if (fetchPromiseRef.current) {
            return fetchPromiseRef.current;
        }

        dispatch({ type: 'FETCH_START' });
        
        const fetchPromise = (async () => {
            try {
                // Via backend proxy (oude base)
                const response = await axios.get(BACKEND_RESERVATIONS_ENDPOINT);
                const allReservations = response.data || [];
                dispatch({ type: 'FETCH_SUCCESS', payload: allReservations, cacheKey });
                return allReservations;
            } catch (error) {
                console.error('Error fetching reservations with draaidag info:', error);
                dispatch({ type: 'FETCH_ERROR', payload: error.message });
                throw error;
            } finally {
                fetchPromiseRef.current = null;
            }
        })();

        fetchPromiseRef.current = fetchPromise;
        return fetchPromise;
    }, [state.cache]);

    // Automatically fetch reservations when the provider mounts
    useEffect(() => {
        fetchReservations();
    }, [fetchReservations]);

    const clearCache = useCallback(() => {
        dispatch({ type: 'CLEAR_CACHE' });
    }, []);

    const [searchParams, setSearchParams] = useSearchParams();
    const initialDraaidagId = searchParams.get('draaidagId');

    const setSelectedDraaidag = useCallback((draaidag) => {
        dispatch({ type: 'SET_SELECTED_DRAAIDAG', payload: draaidag });
        // Update URL when draaidag is selected (without page reload)
        if (draaidag?.id) {
            setSearchParams({ draaidagId: draaidag.id }, { replace: true });
        } else {
            // Remove param when deselecting
            setSearchParams({}, { replace: true });
        }
    }, [setSearchParams]);

    const setCurrentReservation = useCallback((reservation) => {
        dispatch({ type: 'SET_CURRENT_RESERVATION', payload: reservation });
    }, []);

    const updateReservationField = useCallback((reservationId, fieldName, value) => {
        dispatch({ type: 'UPDATE_RESERVATION_FIELD', payload: { reservationId, fieldName, value } });
    }, []);

    // Memoize de context value om onnodige re-renders te voorkomen
    const value = useMemo(() => ({
        ...state,
        fetchReservations,
        setSelectedDraaidag,
        setCurrentReservation,
        clearCache,
        updateReservationField,
        initialDraaidagId
    }), [state, fetchReservations, setSelectedDraaidag, setCurrentReservation, clearCache, updateReservationField, initialDraaidagId]);

    return (
        <MaterialReservationContext.Provider value={value}>
            {children}
        </MaterialReservationContext.Provider>
    );
};

export const useMaterialReservation = () => {
    const context = useContext(MaterialReservationContext);
    if (context === undefined) {
        throw new Error('useMaterialReservation must be used within a MaterialReservationProvider');
    }
    return context;
};
